import { GridRowParams } from '@mui/x-data-grid-premium'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import CardGraphicAndNameCell from 'src/components/DataGrid/cells/CardGraphicAndNameCell/CardGraphicAndNameCell'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import TextAndPillCell from 'src/components/DataGrid/cells/TextAndPillCell/TextAndPillCell'
import UserAvatarCell from 'src/components/DataGrid/cells/UserAvatarCell/UserAvatarCell'
import DataGrid, {
  DataGridRowHeight,
  handleCellClick,
} from 'src/components/DataGrid/DataGrid'
import { CheckboxProps } from 'src/components/VirtualCardsBox/VirtualCardsBox'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import {
  getVirtualCardsTableFieldsCookie,
  setVirtualCardsTableFieldsCookie,
} from 'src/cookies'
import { useTranslate } from 'src/localization/Trns'
import {
  VirtualCardListModel,
  VirtualCardSort,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { connectedPush } from 'src/navigation/Lnk'
import { virtualCardDetailUrl } from 'src/navigation/urls'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { InfiniteScrollProps } from 'src/types'

type VirtualCardsDataGridProps = {
  virtualCards: VirtualCardListModel[]
  virtualCardsPermissions?: Array<{
    permissions: VirtualCardPermissions[]
    virtualCardId: string
  }>
  isYourCards: boolean
  infiniteScrollProps?: InfiniteScrollProps<VirtualCardsFiltersBody>
  filters?: VirtualCardsFiltersBody
  checkboxProps?: CheckboxProps
  clientHeight?: number
  sortableProps?: {
    setSort: (field: string) => void
    sortBy?: string
    sortDescending?: boolean
  }
  defaultColumnVisibilityModel: GridColumnVisibilityModel
  rowSelectionModel?: GridRowSelectionModel
  clearRowSelection?: boolean
}

interface VirtualCardsDataGridRow {
  id: string
  virtualCard: VirtualCardListModel
  index: number
}

const colorForVCN = (
  vc: VirtualCardListModel,
): undefined | 'highlight' | 'severe' => {
  if (vc.isInactive) {
    return 'severe'
  } else if (vc.isActive) {
    return 'highlight'
  }
}

const VirtualCardsDataGrid: React.FC<VirtualCardsDataGridProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const users = useSelector(selectUserListModelsFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { analytics } = useAnalyticsContext()
  const VirtualCardsTableFieldsCookie = getVirtualCardsTableFieldsCookie()
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )

  const [sortModel, setSortModel] = React.useState<GridSortModel>([])

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      cardName:
        VirtualCardsTableFieldsCookie?.cardName ??
        props.defaultColumnVisibilityModel.cardName,
      cardUser:
        VirtualCardsTableFieldsCookie?.cardUser ??
        props.defaultColumnVisibilityModel.cardUser,
      cardType:
        VirtualCardsTableFieldsCookie?.cardType ??
        props.defaultColumnVisibilityModel.cardType,
      status:
        VirtualCardsTableFieldsCookie?.status ??
        props.defaultColumnVisibilityModel.status,
      activeUntil:
        VirtualCardsTableFieldsCookie?.activeUntil ??
        props.defaultColumnVisibilityModel.activeUntil,
      availableCredit:
        VirtualCardsTableFieldsCookie?.availableCredit ??
        props.defaultColumnVisibilityModel.availableCredit,
    })

  const dispatch = useDispatch()

  const rows: VirtualCardsDataGridRow[] = props.virtualCards.map(
    (virtualCard, i) => {
      return {
        id: virtualCard?.id,
        virtualCard: virtualCard,
        index: i,
      }
    },
  )

  const columns: Array<GridColDef<VirtualCardsDataGridRow>> = [
    {
      field: 'cardName',
      headerName: translate('Card Name'),
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <CardGraphicAndNameCell
            text={vc.displayName}
            last4={vc.last4}
            cardTypeImageSrc={vc.cardTypeImageSrc}
            cardTypeAlt={translate(vc.cardTypeLabelSrc)}
            bold
            params={params}
          />
        )
      },
    },
    {
      field: 'cardUser',
      headerName: translate(props.isYourCards ? 'Card Creator' : 'Card User'),
      sortable: false,
      flex: 1,
      minWidth: 150,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <UserAvatarCell
            user={
              props.isYourCards
                ? vc.cardholder ?? users[vc.cardholderId]
                : vc.recipient ?? users[vc.recipientId]
            }
            disabled={vc.isPending || vc.isInactive}
            params={params}
          />
        )
      },
    },
    {
      field: 'cardType',
      headerName: translate('Card Type'),
      sortable: true,
      minWidth: 100,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <TextAndPillCell
            text={vc.cardTypeLabel}
            pillText={vc.features?.singleExactPay ? translate('Exact Pay') : ''}
            disabled={vc.isPending || vc.isInactive}
            params={params}
          />
        )
      },
    },
    {
      field: 'status',
      headerName: translate('Status'),
      sortable: true,
      minWidth: 100,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <DataGridCell
            text={vc.psuedoStatusLabel}
            color={colorForVCN(vc)}
            disabled={vc.isPending}
            params={params}
          />
        )
      },
    },
    {
      field: 'activeUntil',
      headerName: translate('Active Until'),
      sortable: false,
      minWidth: 150,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <DataGridCell
            text={
              vc.isInactive
                ? vc.inactiveSinceTimezonedString
                : vc.activeUntilTimezonedString
            }
            color={colorForVCN(vc)}
            disabled={vc.isPending}
            params={params}
          />
        )
      },
    },
    {
      field: 'availableCredit',
      align: 'right',
      headerName: translate('Available Credit'),
      minWidth: 150,
      sortable: true,
      renderCell: (params): React.ReactElement => {
        const vc = params.row.virtualCard
        return (
          <DataGridCell
            text={vc.balanceLabel}
            color={colorForVCN(vc)}
            disabled={vc.isPending || vc.isInactive}
            params={params}
          />
        )
      },
    },
    chevronColumnDef,
  ]

  useEffect(() => {
    if (sortModel?.length > 0) {
      switch (sortModel?.[0]?.field) {
        case 'cardName':
        default:
          props.sortableProps?.setSort?.(VirtualCardSort.displayName)
          break
        case 'cardType':
          props.sortableProps?.setSort?.(VirtualCardSort.type)
          break
        case 'status':
          props.sortableProps?.setSort?.(VirtualCardSort.status)
          break
        case 'availableCredit': {
          props.sortableProps?.setSort?.(VirtualCardSort.balanceCents)
          break
        }
      }
    }
  }, [sortModel])

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set(selectedVirtualCardIdsFromRedux ?? []),
    })

  useEffect(() => {
    setRowSelectionModel({
      type: 'include',
      ids: new Set(selectedVirtualCardIdsFromRedux ?? []),
    })
  }, [selectedVirtualCardIdsFromRedux])

  return (
    <DataGrid
      height={props.clientHeight}
      rows={rows}
      columns={columns}
      infiniteScrollProps={props.infiniteScrollProps}
      filtersBody={props.filters || {}}
      sortModel={sortModel}
      rowHeight={DataGridRowHeight.Comfy}
      onSortModelChange={(newSortModel): void => setSortModel(newSortModel)}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(
        newModel: GridColumnVisibilityModel,
      ): void => {
        setVirtualCardsTableFieldsCookie(newModel)
        setColumnVisibilityModel(newModel)
        analytics?.track('virtual cards - column selector', {
          screen: calculatedBackgroundLocation.pathname.replace(
            /\/cc_[a-zA-Z0-9]+/,
            '',
          ),
        })
      }}
      disableColumnFilter
      checkboxSelection={props.checkboxProps !== undefined}
      keepNonExistentRowsSelected
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={(selection: GridRowSelectionModel): void => {
        setRowSelectionModel(selection)
        props.checkboxProps?.handleDataGridVirtualCardCheckboxClick(selection)
      }}
      isRowSelectable={(params: GridRowParams): boolean => {
        const virtualCard = params.row.virtualCard
        return !!props.checkboxProps?.canSelectVirtualCard(virtualCard)
      }}
      onCellClick={handleCellClick}
      onRowClick={(row): void => {
        const virtualCard = row.row.virtualCard
        dispatch(
          connectedPush({
            pathname: virtualCardDetailUrl(virtualCard?.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        analytics?.track('virtual cards - select virtual card - item press', {
          intent: 'select virtual card',
          virtualCardSelected: virtualCard.id,
          interaction: 'item press',
        })
      }}
    />
  )
}

export default VirtualCardsDataGrid
